import { COUNTRY_OPTIONS } from './countries'
import {
  countryUsesStateSelect,
  getSubdivisionDisplayLabel,
  isKnownSubdivisionCode,
} from './subdivisions'

import type { CustomerAddress } from '../api/addressApi'

const COUNTRY_LABEL_MAP = new Map(
  COUNTRY_OPTIONS.map((opt) => [opt.value.toLowerCase(), opt.label]),
)

/** The address as the form holds it — the API shape, minus the optional id. */
export type Address = CustomerAddress

/**
 * Hard cap on the address book. At the cap the "Add address" card is not
 * rendered at all, rather than rendered disabled.
 */
export const MAX_ADDRESSES = 10

/** Countries that collect a district instead of a state / province. */
const DISTRICT_COUNTRIES = ['CN']

export function countryUsesDistrict(countryCode: string): boolean {
  return DISTRICT_COUNTRIES.includes(countryCode.trim().toUpperCase())
}

export const EMPTY_ADDRESS: Address = {
  firstName: '',
  lastName: '',
  company: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  state: '',
  district: '',
  countryCode: '',
  phone: '',
  isDefault: false,
}

/**
 * The payload actually sent to the API. `state` and `district` are dropped for
 * countries that don't collect them, so a customer who moves from Texas to the
 * Netherlands doesn't keep `TX` on their record.
 */
export function toAddressPayload(address: Address): Address {
  return {
    ...address,
    state: countryUsesStateSelect(address.countryCode) ? address.state : '',
    district: countryUsesDistrict(address.countryCode) ? address.district : '',
  }
}

// Stable keys for the address card lines — used to attach `data-testid`
// attributes on the rendered spans so Playwright POMs can read each field
// independently.
export type AddressLineKey =
  | 'name'
  | 'street'
  | 'city-state-postcode'
  | 'country'
  | 'phone'

export interface AddressLineEntry {
  key: AddressLineKey
  text: string
}

/** Format an address for the list card — one labelled entry per rendered line. */
export const formatAddressLineEntries = (
  address: Address,
): AddressLineEntry[] => {
  const entries: AddressLineEntry[] = []

  const name = [address.firstName, address.lastName].filter(Boolean).join(' ')
  if (name) entries.push({ key: 'name', text: name })

  const streetLine = [address.addressLine1, address.addressLine2]
    .filter(Boolean)
    .join(' ')
  if (streetLine) entries.push({ key: 'street', text: streetLine })

  // City, State, Postcode line (US/CA: show full subdivision name when code is known)
  const stateLine =
    getSubdivisionDisplayLabel(address.countryCode, address.state) ??
    address.state
  const cityParts = [
    address.city,
    stateLine,
    address.district,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(', ')
  if (cityParts) entries.push({ key: 'city-state-postcode', text: cityParts })

  // Country — show full name, fall back to code if not found
  if (address.countryCode) {
    const countryLabel =
      COUNTRY_LABEL_MAP.get(address.countryCode.toLowerCase()) ??
      address.countryCode
    entries.push({ key: 'country', text: countryLabel })
  }

  if (address.phone) entries.push({ key: 'phone', text: address.phone })

  return entries
}

// Backwards-compatible flat-string view, used by callers that don't need the
// labeled keys (e.g. unit tests).
export const formatAddressLines = (address: Address): string[] =>
  formatAddressLineEntries(address).map((entry) => entry.text)

// Remove a single key from an errors record, returning the same reference if unchanged
export const clearFieldError = (
  errors: Record<string, string>,
  field: string,
): Record<string, string> => {
  if (!errors[field]) return errors
  return Object.fromEntries(
    Object.entries(errors).filter(([key]) => key !== field),
  )
}

/**
 * Every field is required except `addressLine2` and `company`; `state` and
 * `district` are only required where their input is actually shown.
 *
 * Nothing here validates *format* — address look-up lives in Shopify, and the
 * hub deliberately does not second-guess it (RP-5872).
 */
const REQUIRED_FIELDS: {
  name: keyof Address
  errorKey: string
}[] = [
  { name: 'firstName', errorKey: 'addressBook.validation.firstNameRequired' },
  { name: 'lastName', errorKey: 'addressBook.validation.lastNameRequired' },
  {
    name: 'addressLine1',
    errorKey: 'addressBook.validation.addressLine1Required',
  },
  { name: 'postalCode', errorKey: 'addressBook.validation.postcodeRequired' },
  { name: 'city', errorKey: 'addressBook.validation.cityRequired' },
  { name: 'countryCode', errorKey: 'addressBook.validation.countryRequired' },
  { name: 'phone', errorKey: 'addressBook.validation.phoneRequired' },
]

// Validate address fields. Requires a translation function so error messages
// are localised; pass `t` from `useI18N()` at the call site.
export const validateAddress = (
  address: Address,
  t: (key: string) => string,
): Record<string, string> => {
  const errors: Record<string, string> = {}

  REQUIRED_FIELDS.forEach(({ name, errorKey }) => {
    if (!String(address[name] ?? '').trim()) {
      errors[name] = t(errorKey)
    }
  })

  if (countryUsesStateSelect(address.countryCode)) {
    if (!address.state.trim()) {
      errors.state = t('addressBook.validation.stateRequired')
    } else if (!isKnownSubdivisionCode(address.countryCode, address.state)) {
      errors.state = t('addressBook.validation.invalidState')
    }
  }

  if (countryUsesDistrict(address.countryCode) && !address.district.trim()) {
    errors.district = t('addressBook.validation.districtRequired')
  }

  return errors
}
